import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { jsPDF } from "jspdf";
import QRCode from "react-qr-code";
import { 
  Receipt,
  Download,
  Store,
  Calendar,
  CreditCard,
  Loader2,
  AlertCircle,
  Banknote
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { photoStorage, getCategoryIcon } from "../lib/photoStorage";
import { paymentLabel } from "../lib/payments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNIT_LABELS = {
  piece: "pcs",
  litre: "L",
  kg: "kg"
};

export default function PublicInvoice() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [productPhotos, setProductPhotos] = useState({});

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const response = await axios.get(`${API}/public/invoice/${invoiceId}`);
        setInvoice(response.data);
        
        // Load photos for all products in invoice
        if (response.data?.items) {
          const productIds = response.data.items
            .map(item => item.product_id)
            .filter(Boolean);
          if (productIds.length > 0) {
            const photos = await photoStorage.getPhotos(productIds);
            setProductPhotos(photos);
          }
        }
      } catch (err) {
        setError("Facture non trouvée");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [invoiceId]);

  const generatePDF = async () => {
    if (!invoice) return;
    
    setDownloading(true);
    const pdfDevise = invoice.currency_symbol || "€";
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;
      let y = margin;
      
      // Helper functions
      const addText = (text, x, yPos, options = {}) => {
        const { fontSize = 10, fontStyle = 'normal', color = [0, 0, 0], align = 'left' } = options;
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        pdf.setTextColor(...color);
        
        let xPos = x;
        if (align === 'center') {
          xPos = pageWidth / 2;
        } else if (align === 'right') {
          xPos = pageWidth - margin;
        }
        
        pdf.text(text, xPos, yPos, { align });
        return yPos;
      };
      
      const addLine = (yPos, color = [220, 220, 220]) => {
        pdf.setDrawColor(...color);
        pdf.setLineWidth(0.3);
        pdf.line(margin, yPos, pageWidth - margin, yPos);
        return yPos + 2;
      };
      
      const checkPageBreak = (neededSpace) => {
        if (y + neededSpace > pageHeight - 50) { // Reserve 50mm for footer
          pdf.addPage();
          y = margin;
          // Add header on new page
          addText(invoice.boutique_name || invoice.store_name, 0, y, { fontSize: 12, fontStyle: 'bold', align: 'center' });
          y += 8;
          addText(`Facture ${invoice.invoice_number} (suite)`, 0, y, { fontSize: 9, color: [128, 128, 128], align: 'center' });
          y += 10;
          addLine(y);
          y += 5;
        }
      };

      // ========== HEADER ==========
      // Store name
      addText(invoice.boutique_name || invoice.store_name, 0, y, { fontSize: 18, fontStyle: 'bold', align: 'center' });
      y += 7;
      
      // Store details
      if (invoice.store_address) {
        addText(invoice.store_address, 0, y, { fontSize: 9, color: [100, 100, 100], align: 'center' });
        y += 5;
      }
      if (invoice.store_phone) {
        addText(invoice.store_phone, 0, y, { fontSize: 9, color: [100, 100, 100], align: 'center' });
        y += 5;
      }
      
      y += 5;
      y = addLine(y);
      y += 3;
      
      // ========== INVOICE INFO ==========
      const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      };
      
      // Invoice number and date on same line
      addText('Numéro de facture:', margin, y, { fontSize: 9, color: [100, 100, 100] });
      addText(invoice.invoice_number, margin + 40, y, { fontSize: 10, fontStyle: 'bold' });
      
      addText('Date:', pageWidth / 2 + 10, y, { fontSize: 9, color: [100, 100, 100] });
      addText(formatDate(invoice.created_at), pageWidth / 2 + 25, y, { fontSize: 10 });
      y += 6;
      
      // Payment method
      addText('Paiement:', margin, y, { fontSize: 9, color: [100, 100, 100] });
      addText(paymentLabel(invoice) + (invoice.payment_reference ? ` (réf. ${invoice.payment_reference})` : ''), margin + 40, y, { fontSize: 10 });
      y += 8;
      
      y = addLine(y);
      y += 5;
      
      // ========== PRODUCTS TABLE ==========
      // Table header
      pdf.setFillColor(245, 245, 245);
      pdf.rect(margin, y - 2, contentWidth, 8, 'F');
      
      addText('Produit', margin + 12, y + 3, { fontSize: 9, fontStyle: 'bold' });
      addText('Qté', margin + 95, y + 3, { fontSize: 9, fontStyle: 'bold' });
      addText('Prix unit.', margin + 115, y + 3, { fontSize: 9, fontStyle: 'bold' });
      addText('Total', pageWidth - margin - 2, y + 3, { fontSize: 9, fontStyle: 'bold', align: 'right' });
      y += 10;
      
      // Table rows
      invoice.items.forEach((item, index) => {
        checkPageBreak(14);
        
        // Product photo (if available)
        const photo = item.product_id ? productPhotos[item.product_id] : null;
        if (photo) {
          try {
            pdf.addImage(photo, 'JPEG', margin + 1, y - 3, 8, 8);
          } catch (e) {
            console.log('Could not add image to PDF');
          }
        } else {
          // Placeholder square
          pdf.setFillColor(240, 240, 240);
          pdf.rect(margin + 1, y - 3, 8, 8, 'F');
        }
        
        // Product name (truncate if too long)
        let productName = item.product_name;
        if (productName.length > 30) {
          productName = productName.substring(0, 27) + '...';
        }
        addText(productName, margin + 12, y, { fontSize: 9 });
        
        // Barcode (smaller, below product name)
        addText(item.barcode, margin + 12, y + 4, { fontSize: 7, color: [150, 150, 150] });
        
        // Quantity
        addText(`${item.quantity} ${UNIT_LABELS[item.unit] || ''}`, margin + 95, y, { fontSize: 9 });
        
        // Unit price
        addText(`${item.unit_price.toFixed(2)} ${pdfDevise}`, margin + 115, y, { fontSize: 9 });
        
        // Total
        addText(`${item.total.toFixed(2)} ${pdfDevise}`, pageWidth - margin - 2, y, { fontSize: 9, fontStyle: 'bold', align: 'right' });
        
        y += 12;
        
        // Light separator line
        if (index < invoice.items.length - 1) {
          pdf.setDrawColor(240, 240, 240);
          pdf.setLineWidth(0.2);
          pdf.line(margin, y - 2, pageWidth - margin, y - 2);
        }
      });
      
      y += 3;
      y = addLine(y);
      y += 5;
      
      // ========== TOTALS ==========
      // Sous-total
      addText('Sous-total:', margin + 100, y, { fontSize: 10, color: [100, 100, 100] });
      addText(`${invoice.subtotal.toFixed(2)} ${pdfDevise}`, pageWidth - margin - 2, y, { fontSize: 10, align: 'right' });
      y += 6;
      
      // TVA if applicable
      if (invoice.tax > 0) {
        addText('TVA:', margin + 100, y, { fontSize: 10, color: [100, 100, 100] });
        addText(`${invoice.tax.toFixed(2)} ${pdfDevise}`, pageWidth - margin - 2, y, { fontSize: 10, align: 'right' });
        y += 6;
      }
      
      y += 2;
      
      // TOTAL TTC - Big and orange
      pdf.setFillColor(255, 107, 53); // Primary orange
      pdf.roundedRect(margin + 80, y - 2, contentWidth - 80, 14, 2, 2, 'F');
      
      addText('TOTAL TTC', margin + 85, y + 6, { fontSize: 12, fontStyle: 'bold', color: [255, 255, 255] });
      addText(`${invoice.total.toFixed(2)} ${pdfDevise}`, pageWidth - margin - 5, y + 6, { fontSize: 14, fontStyle: 'bold', color: [255, 255, 255], align: 'right' });
      y += 18;
      
      // Payment details (for cash)
      if (invoice.payment_method === 'cash') {
        const amountReceived = invoice.amount_received || invoice.total;
        const change = invoice.change_given || 0;
        
        if (amountReceived > invoice.total || change > 0) {
          y += 2;
          addText('Montant reçu:', margin + 100, y, { fontSize: 9, color: [100, 100, 100] });
          addText(`${amountReceived.toFixed(2)} ${pdfDevise}`, pageWidth - margin - 2, y, { fontSize: 9, align: 'right' });
          y += 5;
          
          addText('Monnaie rendue:', margin + 100, y, { fontSize: 9, color: [100, 100, 100] });
          addText(`${change.toFixed(2)} ${pdfDevise}`, pageWidth - margin - 2, y, { fontSize: 9, align: 'right' });
          y += 8;
        }
      }
      
      // ========== QR CODE ==========
      // Generate QR code as data URL
      const qrCodeUrl = invoice.qr_code_url || `${window.location.origin}/invoice/${invoice.id}`;
      
      // Create a canvas for QR code
      const qrCanvas = document.createElement('canvas');
      const qrSize = 80;
      qrCanvas.width = qrSize;
      qrCanvas.height = qrSize;
      
      // Use a temporary div to render QR code
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      document.body.appendChild(tempDiv);
      
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempDiv);
      
      await new Promise((resolve) => {
        root.render(
          <QRCode 
            value={qrCodeUrl} 
            size={qrSize} 
            level="M"
            style={{ background: 'white' }}
          />
        );
        setTimeout(resolve, 100);
      });
      
      const qrSvg = tempDiv.querySelector('svg');
      if (qrSvg) {
        const svgData = new XMLSerializer().serializeToString(qrSvg);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        
        const img = new Image();
        await new Promise((resolve) => {
          img.onload = resolve;
          img.src = svgUrl;
        });
        
        const canvas = document.createElement('canvas');
        canvas.width = qrSize * 2;
        canvas.height = qrSize * 2;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const qrDataUrl = canvas.toDataURL('image/png');
        
        // Calculate position for QR code (center)
        checkPageBreak(50);
        y += 5;
        y = addLine(y);
        y += 8;
        
        addText('Scannez pour télécharger votre facture', 0, y, { fontSize: 9, color: [100, 100, 100], align: 'center' });
        y += 5;
        
        const qrPdfSize = 35;
        const qrX = (pageWidth - qrPdfSize) / 2;
        pdf.addImage(qrDataUrl, 'PNG', qrX, y, qrPdfSize, qrPdfSize);
        y += qrPdfSize + 5;
        
        URL.revokeObjectURL(svgUrl);
      }
      
      // Cleanup
      root.unmount();
      document.body.removeChild(tempDiv);
      
      // ========== FOOTER ==========
      y += 5;
      y = addLine(y, [255, 107, 53]); // Orange line
      y += 8;
      
      // Thank you message
      addText('Merci pour votre achat !', 0, y, { fontSize: 12, fontStyle: 'bold', color: [255, 107, 53], align: 'center' });
      y += 8;
      
      // Store info footer
      addText(invoice.boutique_name || invoice.store_name, 0, y, { fontSize: 9, fontStyle: 'bold', align: 'center' });
      y += 5;
      
      if (invoice.store_address) {
        addText(invoice.store_address, 0, y, { fontSize: 8, color: [100, 100, 100], align: 'center' });
        y += 4;
      }
      if (invoice.store_phone) {
        addText(`Tél: ${invoice.store_phone}`, 0, y, { fontSize: 8, color: [100, 100, 100], align: 'center' });
      }
      
      // Save PDF
      pdf.save(`facture-${invoice.invoice_number}.pdf`);
      
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full tropical-gradient"></div>
          <p className="text-muted-foreground">Chargement de la facture...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-xl font-bold text-foreground mb-2">Facture non trouvée</h1>
            <p className="text-muted-foreground">
              Cette facture n'existe pas ou a été supprimée.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const devise = invoice.currency_symbol || "€";

  return (
    <div className="min-h-screen bg-background p-4" data-testid="public-invoice-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Receipt className="w-6 h-6 text-primary" />
            <span className="font-bold text-lg">Facture</span>
          </div>
          <Button
            onClick={generatePDF}
            disabled={downloading}
            className="btn-primary"
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Télécharger PDF
          </Button>
        </div>

        {/* Invoice Content */}
        <Card className="border-border/50 overflow-hidden">
          <div style={{ background:'#FFFFFF', color:'#1A1714', padding:'2rem' }} className="md:p-8">
            {/* Store Header — Logo + Name */}
            <div className="text-center border-b pb-6 mb-6" style={{ borderColor:'#E5E5E5' }}>
              {invoice.logo_url ? (
                <img src={invoice.logo_url} alt={invoice.boutique_name || invoice.store_name} style={{ width:'64px', height:'64px', objectFit:'contain', borderRadius:'12px', margin:'0 auto 12px' }} />
              ) : (
                <div style={{ width:'64px', height:'64px', borderRadius:'12px', background:'#FF4500', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
                  <Store style={{ width:'32px', height:'32px', color:'#FFFFFF' }} />
                </div>
              )}
              <h1 style={{ fontSize:'1.5rem', fontWeight:700, color:'#1A1714' }}>{invoice.boutique_name || invoice.store_name}</h1>
              {invoice.store_address && (
                <p style={{ color:'#6B6560', marginTop:'4px' }}>{invoice.store_address}</p>
              )}
              {invoice.store_phone && (
                <p style={{ color:'#6B6560' }}>{invoice.store_phone}</p>
              )}
            </div>

            {/* Invoice Info */}
            <div className="flex flex-wrap justify-between gap-4 mb-6 text-sm">
              <div>
                <p style={{ color:'#6B6560' }}>Numéro de facture</p>
                <p style={{ fontWeight:600, color:'#1A1714' }}>{invoice.invoice_number}</p>
              </div>
              <div>
                <p style={{ color:'#6B6560' }}>Date</p>
                <p style={{ fontWeight:600, color:'#1A1714' }} className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(invoice.created_at)}
                </p>
              </div>
              <div>
                <p style={{ color:'#6B6560' }}>Paiement</p>
                <p style={{ fontWeight:600, color:'#1A1714' }} className="flex items-center gap-1">
                  {invoice.payment_method === 'cash' ? (
                    <Banknote className="w-4 h-4" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  {paymentLabel(invoice)}{invoice.payment_reference ? ` · réf. ${invoice.payment_reference}` : ''}
                </p>
              </div>
            </div>

            {/* Items Table */}
            <div className="border rounded-lg overflow-hidden mb-6" style={{ borderColor:'#E5E5E5' }}>
              <table className="w-full">
                <thead style={{ background:'#F5F5F5' }}>
                  <tr>
                    <th className="text-left p-3 text-sm font-semibold" style={{ color:'#1A1714' }}>Produit</th>
                    <th className="text-center p-3 text-sm font-semibold" style={{ color:'#1A1714' }}>Qté</th>
                    <th className="text-right p-3 text-sm font-semibold" style={{ color:'#1A1714' }}>Prix unit.</th>
                    <th className="text-right p-3 text-sm font-semibold" style={{ color:'#1A1714' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => {
                    const photo = item.product_id ? productPhotos[item.product_id] : null;
                    return (
                      <tr key={index} style={{ borderTop:'1px solid #E5E5E5' }}>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            {photo ? (
                              <img src={photo} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background:'#F5F5F5' }}>
                                <span className="text-lg">{getCategoryIcon(item.category_name)}</span>
                              </div>
                            )}
                            <div>
                              <p style={{ fontWeight:500, color:'#1A1714' }}>{item.product_name}</p>
                              <p style={{ fontSize:'12px', color:'#6B6560' }}>{item.barcode}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-center" style={{ color:'#1A1714' }}>
                          {item.quantity} {UNIT_LABELS[item.unit]}
                        </td>
                        <td className="p-3 text-right" style={{ color:'#1A1714' }}>{item.unit_price.toLocaleString('fr-FR')} {devise}</td>
                        <td className="p-3 text-right" style={{ fontWeight:500, color:'#1A1714' }}>{item.total.toLocaleString('fr-FR')} {devise}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div style={{ borderTop:'1px solid #E5E5E5', paddingTop:'16px' }}>
              <div className="flex justify-between items-center">
                <span style={{ color:'#6B6560' }}>Sous-total</span>
                <span style={{ color:'#1A1714' }}>{invoice.subtotal.toLocaleString('fr-FR')} {devise}</span>
              </div>
              {invoice.tax > 0 && (
                <div className="flex justify-between items-center mt-2">
                  <span style={{ color:'#6B6560' }}>TVA</span>
                  <span style={{ color:'#1A1714' }}>{invoice.tax.toLocaleString('fr-FR')} {devise}</span>
                </div>
              )}
              <div className="flex justify-between items-center mt-4 pt-4" style={{ borderTop:'1px solid #E5E5E5' }}>
                <span style={{ fontSize:'1.25rem', fontWeight:700, color:'#1A1714' }}>Total TTC</span>
                <span style={{ fontSize:'1.5rem', fontWeight:700, color:'#FF4500' }}>
                  {invoice.total.toLocaleString('fr-FR')} {devise}
                </span>
              </div>
              
              {/* Cash payment details */}
              {invoice.payment_method === 'cash' && (invoice.amount_received || invoice.change_given) && (
                <div className="mt-3 pt-3" style={{ borderTop:'1px dashed #E5E5E5' }}>
                  <div className="flex justify-between items-center text-sm">
                    <span style={{ color:'#6B6560' }}>Montant reçu</span>
                    <span style={{ color:'#1A1714' }}>{(invoice.amount_received || invoice.total).toLocaleString('fr-FR')} {devise}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-1">
                    <span style={{ color:'#6B6560' }}>Monnaie rendue</span>
                    <span style={{ color:'#1A1714' }}>{(invoice.change_given || 0).toLocaleString('fr-FR')} {devise}</span>
                  </div>
                </div>
              )}
            </div>

            {/* QR Code */}
            <div className="mt-8 pt-6 flex flex-col items-center" style={{ borderTop:'1px solid #E5E5E5' }}>
              <p style={{ fontSize:'14px', color:'#6B6560', marginBottom:'12px' }}>
                Scannez pour télécharger votre facture
              </p>
              <QRCode
                value={invoice.qr_code_url || `${window.location.origin}/invoice/${invoice.id}`}
                size={120}
                level="M"
              />
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 text-center" style={{ borderTop:'2px solid #FF4500' }}>
              <p style={{ fontSize:'1.125rem', fontWeight:600, color:'#FF4500' }}>Merci pour votre achat !</p>
              <div className="mt-4 text-sm">
                <p style={{ fontWeight:500, color:'#1A1714' }}>{invoice.boutique_name || invoice.store_name}</p>
                {invoice.store_address && <p style={{ color:'#6B6560', marginTop:'4px' }}>{invoice.store_address}</p>}
                {invoice.store_phone && <p style={{ color:'#6B6560' }}>Tél: {invoice.store_phone}</p>}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
